import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Button, Notification, PageTopBar, PageView } from '../../../components'
import { Loader } from '../../../components/Loader'
import { fetchWarehouse } from '../../../services/warehouseSevices'
import { fetchWarehouseConfigurations } from '../../../services/warehouseConfigurationService'
import { PACKING_AUDIT_KEY, isNotAvailable, packingConfigurations } from './packingAuditConfig'
import { retryTransient } from '../../../helpers/retryTransient'
import { PackingAuditCard } from './PackingAuditCard'
import { FIVE_MINUTES_MS } from '../../../helpers/cacheTimes'

// Configuraciones → Empaque: the self-service warehouse keys of the platform's `packing` category.
export const PackingSettingsContainer = () => {
  const { warehouse_id } = useParams()
  const { t } = useTranslation()
  const [notification, setNotification] = useState({ show: false, type: '', title: '' })

  const { data: warehouse } = useQuery(['warehouse', warehouse_id], () => fetchWarehouse(warehouse_id), {
    staleTime: FIVE_MINUTES_MS,
    enabled: !!warehouse_id,
  })
  const configurationsQuery = useQuery(['warehouse_configurations', warehouse_id], () => fetchWarehouseConfigurations(warehouse_id), {
    enabled: !!warehouse_id,
    refetchOnWindowFocus: false,
    retry: retryTransient,
  })
  const auditConfig = packingConfigurations(configurationsQuery.data).find(config => config.key === PACKING_AUDIT_KEY)
  const notify = title => setNotification({ show: true, type: 'success', title })

  const renderBody = () => {
    // A background refetch failing while cached data is still around must not tear down the
    // card — only a first load with nothing cached yet shows the page-level error.
    if (configurationsQuery.isError && !configurationsQuery.data) {
      if (isNotAvailable(configurationsQuery.error)) {
        return <p className="text-sm text-gray-600 py-12 text-center">{t('packing_config.not_available')}</p>
      }
      return (
        <div className="text-center py-12">
          <p className="text-red-600 font-medium mb-3">{t('packing_config.load_error')}</p>
          <Button type="secondary" onClick={() => configurationsQuery.refetch()}>
            {t('packing_config.retry')}
          </Button>
        </div>
      )
    }
    if (!configurationsQuery.data) return <Loader show={true} />
    if (!auditConfig) return <p className="text-sm text-gray-600 py-12 text-center">{t('packing_config.empty')}</p>
    // Keyed on the route param so a URL change to another warehouse remounts the card fresh
    // instead of carrying over its draft/dialog state.
    return <PackingAuditCard key={warehouse_id} warehouseId={warehouse_id} warehouse={warehouse} config={auditConfig} onNotify={notify} />
  }

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <div className="text-lg font-semibold">
            {t('packing_config.title')}
            {warehouse?.name && <span className="ml-2 text-gray-500 font-normal">· {warehouse.name}</span>}
          </div>
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      <Notification
        show={notification.show}
        setShow={val => setNotification(prev => ({ ...prev, show: val }))}
        type={notification.type}
        title={notification.title}
      />
      <div className="p-4 space-y-4 max-w-5xl">{renderBody()}</div>
    </PageView>
  )
}
